/**
 * `/api/agents/analytics` + `/api/agents/analytics/you` — READs for the
 * Agents ▸ Analytics rewrite (#44575).
 *
 * `excellent/app/agents/analytics/page.tsx` is 778 lines of server JSX over
 * five independent reads (`fleet-analytics`, `listWorkerRunsKeyset`,
 * multi-brand `gatherUsage`/`consolidate`, `productDashboardPage("lens")`).
 * There is no client component to hand a prop to, so the reads become this
 * endpoint before the render can become a client route — same shape as
 * `agent-health` (RO-3-13).
 *
 * The You deep-dive home is a separate op: it only needs the lens product
 * dashboard summary (not the fleet ledger). Sibling discrimination in the
 * #44575 oracle relies on page having `roleRows`/`reliability` and you
 * having `label`/`tagline` without those fleet fields.
 *
 * `z.custom` for nested fleet rows — the ledger shapes live in
 * `excellent/src/agents/fleet-analytics.ts` (server-only) and grow
 * additively; a strict zod object would strip the next field silently.
 */

import { z } from "zod";
import type { Operation } from "../types";

export type FleetRangeDto = "7d" | "30d" | "90d" | "all";

const rangeQuery = z.object({
  range: z.enum(["7d", "30d", "90d", "all"]).optional(),
});

/** One multi-brand coding-agent strip row (Claude · Codex · Gemini · Grok). */
export interface CodingAgentPersonalDto {
  id: string;
  label: string;
  sessions: number | null;
  tokens: number | null;
  costUsd: number | null;
  lastActive: string | null;
  coverage: "full" | "partial" | "presence";
}

/** Lens headline stats folded into the hub's "Your coding agents" strip. */
export interface LensStatDto {
  title: string;
  value: string;
  description?: string;
  sparkData?: number[];
  accentColor?: string;
}

/**
 * Full fleet + personal payload for `/agents/analytics`.
 * Field names mirror what the page component destructures.
 */
export interface AgentAnalyticsPageDto {
  range: FleetRangeDto;
  rangeLabel: string;
  summary: Record<string, unknown>;
  daily: {
    points: Array<Record<string, unknown>>;
    roles: string[];
  };
  roleRows: Array<Record<string, unknown>>;
  modelRows: Array<Record<string, unknown>>;
  codingAgentRows: Array<Record<string, unknown>>;
  tools: Array<{ tool: string; calls: number }>;
  goals: Array<Record<string, unknown>>;
  workflows: Array<Record<string, unknown>>;
  workflowShare: number | null;
  reliability: Record<string, unknown>;
  roleEff: Array<Record<string, unknown>>;
  trend: Array<{ date: string; tokPerLanded: number | null }>;
  trace: { total: number; withTrace: number };
  goalShare: number | null;
  landing: Record<string, unknown>;
  codingAgentPersonal: CodingAgentPersonalDto[];
  lensStats: LensStatDto[];
}

export interface AgentAnalyticsYouDto {
  label: string;
  tagline?: string;
  timeScoped: boolean;
  currentRange: "7d" | "30d" | "90d";
  /** Serialisable DashboardSummary from productDashboardPage("lens"). */
  summary: Record<string, unknown>;
}

const pageOp: Operation<
  void,
  z.infer<typeof rangeQuery>,
  void,
  AgentAnalyticsPageDto
> = {
  method: "GET",
  path: () => "/api/agents/analytics",
  query: rangeQuery,
  response: z.custom<AgentAnalyticsPageDto>(),
};

const youOp: Operation<
  void,
  z.infer<typeof rangeQuery>,
  void,
  AgentAnalyticsYouDto
> = {
  method: "GET",
  path: () => "/api/agents/analytics/you",
  query: rangeQuery,
  response: z.custom<AgentAnalyticsYouDto>(),
};

export const agentAnalytics = {
  page: pageOp,
  you: youOp,
};
